import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Hammer,
  Building2,
  Sprout,
  CheckCircle,
  ArrowRight,
  HardHat,
  Home as HomeIcon,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors, images } from "../config/site";

const WORK_AREAS = [
  {
    icon: Hammer,
    title: "Property Rehabilitation",
    body:
      "Full rehabilitation of existing properties — bringing them up to modern, code-compliant, family-ready standards.",
    points: [
      "Structural improvements",
      "Updated electrical & plumbing",
      "Modern kitchens & bathrooms",
      "Energy-efficient upgrades",
    ],
  },
  {
    icon: Building2,
    title: "New Construction",
    body:
      "Ground-up construction of affordable homes designed for working families and long-term durability.",
    points: [
      "Family-focused layouts",
      "Quality materials",
      "Modern construction standards",
      "Warranty included",
    ],
  },
  {
    icon: Sprout,
    title: "Neighborhood Revitalization",
    body:
      "Investing in blocks and corridors that have been underserved — strengthening the surrounding community along the way.",
    points: [
      "Local sourcing where possible",
      "Long-term neighborhood impact",
      "Community-aligned design",
      "Partnership with local stakeholders",
    ],
  },
];

const DevelopmentPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="px-4 pb-14">
          <div className="container mx-auto max-w-6xl">
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: colors.terracotta }}
            >
              Development
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6"
              style={{ color: colors.charcoal }}
            >
              Affordable housing built with{" "}
              <span style={{ color: colors.primary }}>care</span>,{" "}
              <span style={{ color: colors.terracotta }}>quality</span>, and{" "}
              <span style={{ color: colors.sage }}>community in mind</span>.
            </h1>
            <p className="text-lg md:text-xl max-w-3xl" style={{ color: colors.textMuted }}>
              We rehabilitate and develop quality affordable homes. Every project is
              an investment in a neighborhood — and a home that a family can grow in for years to come.
            </p>
          </div>
        </section>

        {/* Real project photos — during + after story */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <p
                  className="text-xs uppercase tracking-widest font-semibold mb-1"
                  style={{ color: colors.terracotta }}
                >
                  From Our Projects
                </p>
                <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: colors.charcoal }}>
                  From rebuild to ready.
                </h2>
              </div>
              <p className="text-sm max-w-md" style={{ color: colors.textMuted }}>
                We take homes that need new life and rebuild them into quality, affordable spaces
                families are proud to call home.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="development-project-gallery">
              <div className="relative rounded-2xl overflow-hidden shadow-md aspect-[4/3]">
                <img
                  src={images.projectFraming}
                  alt="A Dorsey Taylor Homes property mid-rebuild — framing and lumber stage"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                  style={{ backgroundColor: colors.terracotta, color: colors.white }}
                >
                  During
                </span>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-md aspect-[4/3]">
                <img
                  src={images.projectFinishedKitchen}
                  alt="A finished Dorsey Taylor Homes kitchen — white shaker cabinets, subway tile, stainless appliances"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                  style={{ backgroundColor: colors.sage, color: colors.white }}
                >
                  After
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Work Areas */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-12">
              <p
                className="text-xs uppercase tracking-widest font-semibold mb-3"
                style={{ color: colors.terracotta }}
              >
                What we build
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
                From full rehab to new construction.
              </h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              {WORK_AREAS.map(({ icon: Icon, title, body, points }) => (
                <div
                  key={title}
                  className="rounded-3xl p-7 shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.white, borderTop: `3px solid ${colors.terracotta}` }}
                  data-testid={`dev-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: colors.charcoal }}>
                    {title}
                  </h3>
                  <p className="mb-5 text-sm" style={{ color: colors.textMuted }}>
                    {body}
                  </p>
                  <ul className="space-y-2">
                    {points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm" style={{ color: colors.charcoal }}>
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.sage }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="px-4 pb-20" style={{ backgroundColor: colors.ivory }}>
          <div className="container mx-auto max-w-6xl py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-3xl overflow-hidden shadow-md">
                <img
                  src={images.projectFinishedOpen}
                  alt="A finished Dorsey Taylor Homes interior — wood floors, fresh paint, refinished staircase, modern lighting"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <p
                  className="text-xs uppercase tracking-widest font-semibold mb-3"
                  style={{ color: colors.terracotta }}
                >
                  Our Approach
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold mb-5" style={{ color: colors.charcoal }}>
                  Affordable does not mean compromised.
                </h2>
                <p className="mb-4" style={{ color: colors.textMuted }}>
                  Our development work prioritizes long-term quality, energy efficiency, and family-first
                  design. We focus on the systems that matter most — structural integrity, durable finishes,
                  and modern essentials — so that families inherit a home, not a project.
                </p>
                <div className="flex flex-wrap gap-3 mt-5">
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
                  >
                    <HardHat className="inline w-3 h-3 mr-1.5 -mt-0.5" />
                    Quality Construction
                  </span>
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: colors.sage }}
                  >
                    Energy Efficient
                  </span>
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: "rgba(63,93,74,0.1)", color: colors.primary }}
                  >
                    <HomeIcon className="inline w-3 h-3 mr-1.5 -mt-0.5" />
                    Family-Centered Design
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4">
          <div className="container mx-auto max-w-5xl">
            <div
              className="rounded-3xl p-10 text-center shadow-md"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              <h2 className="text-2xl lg:text-3xl font-bold mb-3">
                Have a property, partnership, or community project in mind?
              </h2>
              <p className="mb-6 opacity-90 max-w-2xl mx-auto">
                We work with property owners, community organizations, and housing partners on
                affordable development opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact" data-testid="dev-contact-cta">
                  <Button
                    className="rounded-full px-7 py-6 font-semibold"
                    style={{ backgroundColor: colors.terracotta, color: colors.white }}
                  >
                    Start a Conversation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/partnerships" data-testid="dev-partnerships-cta">
                  <Button
                    variant="outline"
                    className="rounded-full px-7 py-6 font-semibold border-2"
                    style={{ borderColor: colors.white, color: colors.white, backgroundColor: "transparent" }}
                  >
                    Partnership Opportunities
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DevelopmentPage;
